// 클립보드 공유(설계서 §9.3.1) — "clip" DataChannel 하나를 감싼다(에이전트가 만든 채널을 ondatachannel 로 받는다).
//
// - put(content, paste): 내 내용을 원격 클립보드에 넣는다. paste=true 면 에이전트가 **쓴 뒤에** 붙여넣기 단축키를 누른다.
// - get(copy): 원격 클립보드를 가져온다. copy=true 면 에이전트가 복사 단축키를 먼저 누르고 **바뀐 경우에만** 준다.
// 작업은 한 줄로 세운다(에이전트는 수신을 한 건씩 처리한다 — 동시에 보내면 앞 전송이 버려진다).
import {
  CLIP_ERR,
  CLIP_MSG,
  ClipError,
  Incoming,
  checkSendable,
  encodeFrames,
  newClipId,
  parseHello,
  type ClipCaps,
  type ClipContent,
} from './protocol';

/** 보내기 대기열 상한 — 넘으면 비워질 때까지 기다린다(수 MB 를 한 번에 밀어 넣지 않는다). */
const HIGH_WATER = 1024 * 1024;
const LOW_WATER = 256 * 1024;
/** 마지막 조각을 보낸 뒤 적용 결과(ack)를 기다리는 시간. */
const ACK_TIMEOUT_MS = 15_000;
/** get 전체 대기 시간(복사 대기 1.5초 + 읽기 + 전송). */
const GET_TIMEOUT_MS = 20_000;

interface Waiter {
  resolve(value: ClipContent | undefined): void;
  reject(e: ClipError): void;
  timer?: number;
}

export class ClipChannel {
  private caps: ClipCaps | null = null;
  private readonly waiters = new Map<string, Waiter>();
  private incoming: Incoming | null = null;
  private queue: Promise<unknown> = Promise.resolve();

  /**
   * @param dc     에이전트가 만든 clip 채널
   * @param onCaps 에이전트 hello 수신 — 상위가 버튼·단축키를 켠다
   */
  constructor(
    private readonly dc: RTCDataChannel,
    private readonly onCaps: (caps: ClipCaps) => void,
  ) {
    dc.bufferedAmountLowThreshold = LOW_WATER;
    dc.onmessage = (ev) => this.onMessage(ev.data);
    dc.onclose = () => this.failAll(CLIP_ERR.CLOSED);
  }

  /** 에이전트가 알린 능력(hello 전이면 null). */
  get capabilities(): ClipCaps | null {
    return this.caps;
  }

  /** 내 내용을 원격 클립보드에 넣는다. */
  put(content: ClipContent, paste: boolean): Promise<void> {
    return this.enqueue(async () => {
      const caps = this.requireOpen();
      checkSendable(content, caps);
      const id = newClipId();
      // 기다림을 **먼저** 등록한다 — 에이전트가 첫 조각에서 거절(상한·제어 꺼짐)하면 그 ack 가 전송 중에 온다.
      const done = this.wait(id);
      try {
        await this.sendFrames(id, encodeFrames(id, content, paste));
      } catch (e) {
        this.settle(id, e instanceof ClipError ? e : new ClipError(CLIP_ERR.CLOSED));
      }
      this.arm(id, ACK_TIMEOUT_MS);
      await done;
    });
  }

  /** 원격 클립보드를 가져온다. */
  get(copy: boolean): Promise<ClipContent> {
    return this.enqueue(async () => {
      this.requireOpen();
      const id = newClipId();
      const done = this.wait(id);
      this.arm(id, GET_TIMEOUT_MS);
      this.dc.send(JSON.stringify({ t: CLIP_MSG.GET, id, copy }));
      const c = await done;
      if (!c) {
        throw new ClipError(CLIP_ERR.PROTOCOL, '내용 없이 끝났습니다');
      }
      return c;
    });
  }

  /**
   * 원격 클립보드를 **그대로** 원격에 붙여넣게 한다(쓰지 않고 원격 OS 의 붙여넣기 단축키만).
   * 원격에서 방금 복사한 것을 원격 안에서 붙일 때 — 내 클립보드로 덮으면 원격의 파일·서식 복사가 사라진다.
   * 에이전트가 `remotePaste` 를 알리지 않았으면 UNSUPPORTED 로 던진다(호출측이 미리 본다).
   */
  pasteRemote(): Promise<void> {
    return this.enqueue(async () => {
      const caps = this.requireOpen();
      if (!caps.remotePaste) {
        throw new ClipError(CLIP_ERR.UNSUPPORTED);
      }
      const id = newClipId();
      const done = this.wait(id);
      this.arm(id, ACK_TIMEOUT_MS);
      this.dc.send(JSON.stringify({ t: CLIP_MSG.PASTE, id }));
      await done;
    });
  }

  private requireOpen(): ClipCaps {
    if (!this.caps || this.dc.readyState !== 'open') {
      throw new ClipError(CLIP_ERR.CLOSED);
    }
    return this.caps;
  }

  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.queue.then(fn, fn);
    this.queue = run.catch(() => undefined);
    return run;
  }

  private wait(id: string): Promise<ClipContent | undefined> {
    return new Promise((resolve, reject) => this.waiters.set(id, { resolve, reject }));
  }

  /** 기다림에 시간 제한을 건다. 넘으면 실패 처리하고 에이전트에 취소를 알린다. */
  private arm(id: string, ms: number): void {
    const w = this.waiters.get(id);
    if (!w) {
      return;
    }
    w.timer = window.setTimeout(() => {
      this.settle(id, new ClipError(CLIP_ERR.TIMEOUT));
      this.trySend(JSON.stringify({ t: CLIP_MSG.CANCEL, id }));
    }, ms);
  }

  private settle(id: string, error?: ClipError, value?: ClipContent): void {
    const w = this.waiters.get(id);
    if (!w) {
      return;
    }
    this.waiters.delete(id);
    window.clearTimeout(w.timer);
    if (error) {
      w.reject(error);
    } else {
      w.resolve(value);
    }
  }

  private failAll(code: string): void {
    for (const id of [...this.waiters.keys()]) {
      this.settle(id, new ClipError(code));
    }
    this.incoming = null;
  }

  private trySend(text: string): void {
    if (this.dc.readyState === 'open') {
      this.dc.send(text);
    }
  }

  /** 조각 열을 흐름 제어하며 보낸다. 에이전트가 이미 거절했으면(기다림이 끝났으면) 나머지는 보내지 않는다. */
  private async sendFrames(id: string, frames: string[]): Promise<void> {
    for (const f of frames) {
      if (!this.waiters.has(id)) {
        return;
      }
      if (this.dc.readyState !== 'open') {
        throw new ClipError(CLIP_ERR.CLOSED);
      }
      if (this.dc.bufferedAmount > HIGH_WATER) {
        await this.drained();
      }
      this.dc.send(f);
    }
  }

  /** 보내기 대기열이 LOW_WATER 아래로 내려가거나 채널이 닫힐 때까지. */
  private drained(): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        this.dc.removeEventListener('bufferedamountlow', done);
        this.dc.removeEventListener('close', done);
        resolve();
      };
      this.dc.addEventListener('bufferedamountlow', done);
      this.dc.addEventListener('close', done);
    });
  }

  private onMessage(data: unknown): void {
    if (typeof data !== 'string') {
      return;
    }
    let v: Record<string, unknown>;
    try {
      v = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return;
    }
    const id = typeof v.id === 'string' ? v.id : '';
    switch (v.t) {
      case CLIP_MSG.HELLO: {
        const caps = parseHello(v);
        if (caps) {
          this.caps = caps;
          this.onCaps(caps);
        }
        return;
      }
      case CLIP_MSG.ACK:
        this.settle(id, v.ok === true ? undefined : new ClipError(String(v.err ?? CLIP_ERR.FAILED)));
        return;
      case CLIP_MSG.PUT: {
        if (!this.waiters.has(id) || !this.caps) {
          return; // 요청한 적 없는 전송
        }
        try {
          this.incoming = Incoming.begin(v, this.caps);
        } catch (e) {
          this.incoming = null;
          this.settle(id, e as ClipError);
          this.trySend(JSON.stringify({ t: CLIP_MSG.CANCEL, id }));
        }
        return;
      }
      case CLIP_MSG.DATA: {
        const inc = this.incoming;
        if (!inc || inc.id !== id) {
          return;
        }
        try {
          inc.push(v.seq, v.d);
        } catch (e) {
          this.incoming = null;
          this.settle(id, e as ClipError);
          this.trySend(JSON.stringify({ t: CLIP_MSG.CANCEL, id }));
        }
        return;
      }
      case CLIP_MSG.END: {
        const inc = this.incoming;
        if (!inc || inc.id !== id) {
          return;
        }
        this.incoming = null;
        try {
          this.settle(id, undefined, inc.finish());
        } catch (e) {
          this.settle(id, e as ClipError);
        }
        return;
      }
      default:
        return;
    }
  }
}
